import { useField, useFormikContext } from 'formik';
import React, { useEffect, useReducer, useRef, useState } from 'react';

import styled from '@emotion/styled';

import useOutsideClick from '../../hooks/ui-control/useOutsideClick';
import ChevronFormDown from '../Icons/ChevronFormDown';
import ChevronFormUp from '../Icons/ChevronFormUp';
import { FieldErrorInfo, Input, InputLabel, InputWrapper } from './FormField';

interface CustomSearchableSelectFieldProps {
  label?: any;
  id: string;
  name: string;
  placeholder?: string;
  className?: string;
  onChange?: (value: string) => void;
  optionData: string[];
}

// Type definition
export enum OptionReducerActionType {
  CLICKED_OPTION = 'option-clicked',
}

interface IOptionReducerActionType {
  type: OptionReducerActionType;
  payload: { index: number };
}

// Reducers
function selectOptionsReducer(
  state: { index: number },
  action: IOptionReducerActionType,
) {
  switch (action.type) {
    case OptionReducerActionType.CLICKED_OPTION:
      return { ...action.payload };
    default:
      return state;
  }
}

export const CustomSearchableSelectField: React.FC<
  CustomSearchableSelectFieldProps
> = ({ label, className, optionData = [], onChange, ...props }) => {
  const selectFieldRef = useRef<HTMLDivElement>(null);
  useOutsideClick(selectFieldRef, () => setDidTouchSelectField(false));

  const { handleBlur } = useFormikContext<any>();
  const [field, meta, helper] = useField(props);
  const [selectFieldValue, setSelectFieldValue] = useState<string>('');
  const [didTouchSelectField, setDidTouchSelectField] =
    useState<boolean>(false);
  const [inputText, setInputText] = useState<string>('');

  useEffect(() => {
    setSelectFieldValue(field.value ?? '');
    setInputText(field.value ?? '');
  }, [field.value]);

  const handleSelectFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSelectFieldValue(value);
    setInputText(value);
    setDidTouchSelectField(true);
    // Reset the selected index when filtering changes
    dispatch({
      type: OptionReducerActionType.CLICKED_OPTION,
      payload: { index: -1 },
    });
  };

  const handleSelectFieldClick = (e: any) => {
    setDidTouchSelectField(true);
  };

  const handleOptionClick = (e: any) => {
    const value = e.target.innerText;
    helper.setValue(value);
    setSelectFieldValue(value);
    setInputText(value);
    setDidTouchSelectField(false);
    if (onChange) onChange(value);
  };

  const handIconClick = (e: any) => {
    setDidTouchSelectField(!didTouchSelectField);
  };

  // Filter options based on input text
  const filteredOptions = optionData.filter((option) =>
    option.toLowerCase().includes(inputText.toLowerCase()),
  );

  const [state, dispatch] = useReducer(selectOptionsReducer, {
    index: -1,
  });

  const handleOptionStateChange = (index: any) => {
    dispatch({
      type: OptionReducerActionType.CLICKED_OPTION,
      payload: { index },
    });

    return handleOptionClick;
  };

  return (
    <InputWrapper className={className}>
      {label && (
        <InputLabel as='div' htmlFor={props.name || props.id}>
          {label}
        </InputLabel>
      )}
      <InputContainer ref={selectFieldRef}>
        <SelectFieldBox
          placeholder={props.placeholder ?? ''}
          type='text'
          onChange={handleSelectFieldChange}
          value={selectFieldValue}
          name={props.name}
          id={props.name}
          onBlur={handleBlur}
          onClick={handleSelectFieldClick}
          className={didTouchSelectField ? 'active' : ''}
        />

        {didTouchSelectField ? (
          <IconStyle onClick={handIconClick}>
            <ChevronFormUp />
          </IconStyle>
        ) : (
          <IconStyle onClick={handIconClick}>
            <ChevronFormDown />
          </IconStyle>
        )}

        {didTouchSelectField && (
          <SelectOptionBox>
            {filteredOptions.length !== 0 ? (
              filteredOptions.map((data, index) => {
                const isSelected = index === state.index ? true : false;
                return (
                  <SelectOption
                    key={index}
                    handleOptionClick={(e) => {
                      handleOptionClick(e);
                      handleOptionStateChange(index);
                    }}
                    isSelected={isSelected}
                  >
                    {data}
                  </SelectOption>
                );
              })
            ) : (
              <EmptyState>
                <em>No matching options found</em>
              </EmptyState>
            )}
          </SelectOptionBox>
        )}
      </InputContainer>
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{meta.error.capitalize()}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default CustomSearchableSelectField;

interface SelectOptionProps {
  handleOptionClick: (...args: any) => void;
  isSelected: boolean;
  children: string;
}

export const SelectOption: React.FC<SelectOptionProps> = ({
  handleOptionClick,
  isSelected,
  children,
}) => {
  return (
    <Option
      onClick={handleOptionClick}
      className={isSelected ? 'isSelected' : ''}
    >
      {children}
    </Option>
  );
};

const SelectFieldBox = styled(Input)`
  width: 100%;
  min-width: 0;
  padding-right: 36px;
  &.active {
    border-color: ${({ theme }) => theme.palette.logiratePrimary};
  }
`;

export const InputContainer = styled.div`
  display: grid;
  width: 100%;
  min-width: 0;
  height: fit-content;
  position: relative;
  height: fit-content;

  & > * {
    grid-column: 1;
    grid-row: 1;
  }
  &:hover {
    cursor: pointer;
  }
`;

export const SelectOptionBox = styled.div`
  position: absolute;
  top: 100%;
  width: 100%;
  max-height: 180px;
  display: flex;
  overflow: auto;
  flex-direction: column;
  gap: 4px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  box-shadow: 1px 4px 12px -1px rgba(44, 78, 39, 0.15);
  border-radius: 4px;
  background-color: white;
  margin-top: 4px;
  z-index: 10;
  color: ${({ theme }) => theme.palette.grayTextColor};

  /* border: 1px solid red; */
`;

export const Option = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  padding: 16px;
  font-size: 14px;
  /* border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor}; */

  &:hover {
    background-color: #f9fafb;
  }

  &:last-of-type {
    border: none;
  }

  &.isSelected {
    background-color: #f9fafb;
  }
`;

const IconStyle = styled.div`
  margin-left: auto;
  margin-top: auto;
  margin-bottom: auto;
  margin-right: 13px;
  z-index: 8;
`;

const EmptyState = styled.div`
  display: flex;
  padding: 16px;
  cursor: not-allowed;
  font-weight: 500;
  font-size: 14px;
  color: gray;
  align-items: center;
  justify-content: center;
  width: 100%;
`;
